import { DateTime } from 'luxon';

export const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

export function getEventStatusColors(status) {
  switch (status) {
    case 'Hold':
      return { bg: '#fef3c7', color: '#92400e', border: '#fbbf24' };
    case 'In_Progress':
      return { bg: '#dbeafe', color: '#1e40af', border: '#60a5fa' };
    case 'Complete':
      return { bg: '#dcfce7', color: '#166534', border: '#34d399' };
    case 'Canceled':
      return { bg: '#fee2e2', color: '#991b1b', border: '#ef4444' };
    default:
      return { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' };
  }
}

// Get date range for last 7 days (including today)
export function getLastSevenDaysRange() {
  const now = new Date();
  const sevenDaysAgo = new Date(now);
  sevenDaysAgo.setDate(now.getDate() - 6); // 6 days ago to include today
  sevenDaysAgo.setHours(0, 0, 0, 0);
  
  return {
    start: sevenDaysAgo,
    end: now,
    formatted: `${sevenDaysAgo.toLocaleDateString()} - ${now.toLocaleDateString()}`
  };
}

// Get date range for current month
export function getCurrentMonthRange() {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  return {
    start: startOfMonth,
    end: endOfMonth,
    formatted: `${startOfMonth.toLocaleDateString()} - ${endOfMonth.toLocaleDateString()}`
  };
}

// Format date to YYYY-MM-DD
export function formatDateToYYYYMMDD(date) {
  return date.toISOString().slice(0, 10);
}

export const convertLocalDateToUTC = (localDate) => {
  return DateTime.fromFormat(localDate, 'yyyy-MM-dd').toUTC().toISODate();
};

export const getTimezoneForLocation = async (lat, lng) => {
  try {
    const timestamp = Math.floor(Date.now() / 1000);
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${process.env.REACT_APP_GOOGLE_TIMEZONE_API_KEY}`
    );
    const data = await response.json();
    return data.timeZoneId;
  } catch (error) {
    console.error('Error fetching timezone:', error);
    return 'UTC';
  }
};

export const convertUTCToTimezone = (utcDateString, timezone) => {
  try {
    const cleanTimezone = timezone?.replace('::text', '') || 'UTC';
    return DateTime.fromISO(utcDateString).setZone(cleanTimezone).toJSDate();
  } catch (error) {
    console.error('Error converting timezone:', error);
    return new Date(utcDateString);
  }
};

export const convertTimezoneToUTC = (date, timezone) => {
  const cleanTimezone = timezone?.replace('::text', '') || 'UTC';
  return DateTime.fromJSDate(date).setZone(cleanTimezone).toUTC().toISO();
};

export const formatDateForDisplay = (utcDateString, timezone) => {
  try {
    const cleanTimezone = timezone?.replace(/['"]/g, '').replace('::text', '') || 'UTC';
    return DateTime.fromISO(utcDateString)
      .setZone(cleanTimezone)
      .toFormat('MM/dd/yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

export const formatTimeForDisplay = (utcDateString, timezone) => {
  try {
    const cleanTimezone = timezone?.replace(/['"]/g, '').replace('::text', '') || 'UTC';
    const dt = DateTime.fromISO(utcDateString).setZone(cleanTimezone);
    return dt.toFormat('h:mm a ZZZZ');
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
};

export const createUTCFromTimezone = (dateString, timeString, timezone) => {
  try {
    const cleanTimezone = timezone?.replace('::text', '') || 'UTC';
    return DateTime.fromFormat(`${dateString}T${timeString}`, 'yyyy-MM-ddTHH:mm', { zone: cleanTimezone })
      .toUTC()
      .toISO();
  } catch (error) {
    console.error('Error creating UTC date:', error);
    return DateTime.now().toUTC().toISO();
  }
};

// Add this new function to utils.js
export const formatDateRangeForDisplay = (startTime, endTime, timezone) => {
  try {
    const cleanTimezone = timezone?.replace(/['"]/g, '').replace('::text', '') || 'UTC';
    const start = DateTime.fromISO(startTime).setZone(cleanTimezone);
    const end = DateTime.fromISO(endTime).setZone(cleanTimezone);
    
    // Check if it's a multi-day event
    const isMultiDay = !start.hasSame(end, 'day');
    
    if (isMultiDay) {
      // For multi-day events, show date range
      return `${start.toFormat('MM/dd/yyyy')} - ${end.toFormat('MM/dd/yyyy')}`;
    } else {
      // For single-day events, show just the date
      return start.toFormat('MM/dd/yyyy');
    }
  } catch (error) {
    console.error('Error formatting date range:', error);
    return 'Invalid date';
  }
};

export const formatTimeRangeForDisplay = (startTime, endTime, timezone) => {
  try {
    const cleanTimezone = timezone?.replace(/['"]/g, '').replace('::text', '') || 'UTC';
    const start = DateTime.fromISO(startTime).setZone(cleanTimezone);
    const end = DateTime.fromISO(endTime).setZone(cleanTimezone);
    
    // Check if it's a multi-day event
    const isMultiDay = !start.hasSame(end, 'day');
    
    if (isMultiDay) {
      // For multi-day events, show full date and time for both
      return `${start.toFormat('h:mm a')} (${start.toFormat('MM/dd')}) - ${end.toFormat('h:mm a')} (${end.toFormat('MM/dd')}) ${start.toFormat('ZZZZ')}`;
    } else {
      // For single-day events, show just the time range
      return `${start.toFormat('h:mm a')} - ${end.toFormat('h:mm a')} ${start.toFormat('ZZZZ')}`;
    }
  } catch (error) {
    console.error('Error formatting time range:', error);
    return 'Invalid time';
  }
};

// ===============================
// ID GENERATION UTILITIES
// ===============================

/**
 * Simple hash function to generate consistent hash from string
 * Uses djb2 algorithm for consistent results
 */
function simpleHash(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
  }
  return Math.abs(hash);
}

/**
 * Extract state abbreviation from location string using Google Places format
 * @param {string} location - Full address from Google Places
 * @returns {string} - Two letter state abbreviation or 'XX' if not found
 */
function extractStateFromLocation(location) {
  if (!location) return 'XX';
  
  // Common state abbreviation patterns in Google Places responses
  const statePatterns = [
    // Match ", CA " or ", CA," or ", CA"
    /,\s*([A-Z]{2})[\s,]/,
    // Match "California" and convert to abbreviation
    /,\s*(Alabama|Alaska|Arizona|Arkansas|California|Colorado|Connecticut|Delaware|Florida|Georgia|Hawaii|Idaho|Illinois|Indiana|Iowa|Kansas|Kentucky|Louisiana|Maine|Maryland|Massachusetts|Michigan|Minnesota|Mississippi|Missouri|Montana|Nebraska|Nevada|New Hampshire|New Jersey|New Mexico|New York|North Carolina|North Dakota|Ohio|Oklahoma|Oregon|Pennsylvania|Rhode Island|South Carolina|South Dakota|Tennessee|Texas|Utah|Vermont|Virginia|Washington|West Virginia|Wisconsin|Wyoming)[\s,]/i
  ];

  // State name to abbreviation mapping
  const stateMap = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
    'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
    'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
    'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
    'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
    'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
    'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
    'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
    'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
    'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
    'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
    'wisconsin': 'WI', 'wyoming': 'WY'
  };

  // Try to match state abbreviation first
  const abbrevMatch = location.match(statePatterns[0]);
  if (abbrevMatch) {
    return abbrevMatch[1].toUpperCase();
  }

  // Try to match full state name
  const fullNameMatch = location.match(statePatterns[1]);
  if (fullNameMatch) {
    return stateMap[fullNameMatch[1].toLowerCase()] || 'XX';
  }

  return 'XX';
}

/**
 * Extract ZIP code from location string
 * @param {string} location - Full address from Google Places
 * @returns {string} - ZIP code or '00000' if not found
 */
function extractZipFromLocation(location) {
  if (!location) return '00000';
  
  // Match 5-digit or 9-digit ZIP codes
  const zipMatch = location.match(/\b(\d{5}(?:-\d{4})?)\b/);
  if (zipMatch) {
    return zipMatch[1].split('-')[0]; // Return just the 5-digit part
  }
  
  return '00000';
}

/**
 * Generate unique Event ID based on location and event details
 * Format: {STATE}{ZIPCODE}{FIRST_3_LETTERS_FROM_EVENT_NAME}{HASH_OF_UUID}
 * @param {string} eventTitle - The event title
 * @param {string} location - Full address from Google Places
 * @param {string} eventUuid - The unique event UUID from database
 * @returns {string} - Generated event ID
 */
export function generateEventId(eventTitle, location, eventUuid) {
  if (!eventTitle) throw new Error('Event title is required for ID generation');
  if (!eventUuid) throw new Error('Event UUID is required for ID generation');
  
  const state = extractStateFromLocation(location);
  const zipCode = extractZipFromLocation(location);
  
  // Extract first 3 letters from event name (remove special characters and spaces)
  const cleanTitle = eventTitle.replace(/[^a-zA-Z]/g, '').toUpperCase();
  const titlePrefix = cleanTitle.substring(0, 3).padEnd(3, 'X'); // Pad with X if less than 3 letters
  
  // Generate a short hash from the UUID for uniqueness
  const uuidHash = simpleHash(eventUuid);
  const hashSuffix = uuidHash.toString(36).toUpperCase().substring(0, 4); // 4 character hash
  
  return `${state}${zipCode}${titlePrefix}${hashSuffix}`;
}

/**
 * Generate unique Company ID based on company name hash
 * Creates consistent hash that will be the same for the same company name
 * @param {string} companyName - The company name
 * @returns {string} - Generated company ID (8 character hash)
 */
export function generateCompanyId(companyName) {
  if (!companyName || !companyName.trim()) {
    throw new Error('Company name is required for ID generation');
  }
  
  // Normalize company name (lowercase, remove special chars, trim spaces)
  const normalizedName = companyName.toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim();
  
  if (!normalizedName) {
    throw new Error('Company name must contain valid characters for ID generation');
  }
  
  // Generate hash and convert to base 36 for alphanumeric result
  const hash = simpleHash(normalizedName);
  const companyId = hash.toString(36).toUpperCase().substring(0, 8).padStart(8, '0');
  
  return `CO${companyId}`;
}

/**
 * Generate unique Employee/Operator ID 
 * Uses user's email hash + first 2 letters of name for uniqueness
 * @param {string} email - User's email address
 * @param {string} name - User's full name
 * @returns {string} - Generated employee ID
 */
export function generateEmployeeId(email, name) {
  if (!email) throw new Error('Email is required for employee ID generation');
  
  // Normalize email (lowercase)
  const normalizedEmail = email.toLowerCase().trim();
  
  // Generate hash from email for uniqueness
  const emailHash = simpleHash(normalizedEmail);
  const hashStr = emailHash.toString(36).toUpperCase().substring(0, 6).padStart(6, '0');
  
  // Get initials from name if available
  let namePrefix = 'XX';
  if (name && name.trim()) {
    const cleanName = name.replace(/[^a-zA-Z\s]/g, '').trim();
    if (cleanName) {
      const nameParts = cleanName.split(' ').filter(part => part.length > 0);
      if (nameParts.length >= 2) {
        namePrefix = (nameParts[0][0] + nameParts[1][0]).toUpperCase();
      } else if (nameParts.length === 1) {
        namePrefix = (nameParts[0].substring(0, 2)).toUpperCase().padEnd(2, 'X');
      }
    }
  }
  
  return `EMP${namePrefix}${hashStr}`;
}

/**
 * Validate generated IDs to ensure they meet format requirements
 */
export function validateEventId(eventId) {
  // Should be: 2 letter state + 5 digit zip + 3 letters + 4 letters
  return /^[A-Z]{2}\d{5}[A-Z]{3}[A-Z0-9]{4}$/.test(eventId);
}

export function validateCompanyId(companyId) {
  // Should be: CO + 8 alphanumeric characters
  return /^CO[A-Z0-9]{8}$/.test(companyId);
}

export function validateEmployeeId(employeeId) {
  // Should be: EMP + 2 letters + 6 alphanumeric characters
  return /^EMP[A-Z]{2}[A-Z0-9]{6}$/.test(employeeId);
}

/**
 * Utility function to generate all IDs at once from event data
 * @param {Object} eventData - Event data object
 * @returns {Object} - Object containing all generated IDs
 */
export function generateAllEventIds(eventData) {
  const ids = {};
  
  try {
    // Generate event ID
    if (eventData.title && eventData.location) {
      ids.eventId = generateEventId(eventData.title, eventData.location, eventData.uuid);
    }
    
    // Generate company ID if company name exists
    if (eventData.company_name) {
      ids.companyId = generateCompanyId(eventData.company_name);
    }
    
    // Generate employee IDs for assigned team members
    if (eventData.assignedTeam && Array.isArray(eventData.assignedTeam)) {
      ids.employeeIds = eventData.assignedTeam.map(member => {
        if (member.email && member.name) {
          return {
            userId: member.id || member.user_id,
            employeeId: generateEmployeeId(member.email, member.name),
            name: member.name,
            email: member.email
          };
        }
        return null;
      }).filter(Boolean);
    }
    
    return ids;
  } catch (error) {
    console.error('Error generating IDs:', error);
    return { error: error.message };
  }
}


/**
 * Sanitize filename for Supabase storage
 * Removes or replaces characters that are not allowed in storage keys
 * @param {string} filename - Original filename
 * @returns {string} - Sanitized filename safe for storage
 */
export function sanitizeFilename(filename) {
  if (!filename) return 'unnamed-file';
  
  // Get file extension
  const lastDotIndex = filename.lastIndexOf('.');
  const name = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
  const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';
  
  // Replace problematic characters with safe alternatives
  const sanitizedName = name
    .replace(/\s+/g, '_')           // Replace spaces with underscores
    .replace(/[^\w\-_.]/g, '')      // Remove all non-alphanumeric except dash, underscore, dot
    .replace(/_{2,}/g, '_')         // Replace multiple underscores with single
    .replace(/^_+|_+$/g, '')        // Remove leading/trailing underscores
    .substring(0, 100);             // Limit length to prevent overly long names
  
  // Ensure we have a valid name
  const finalName = sanitizedName || 'file';
  
  return `${finalName}${extension}`;
}

export const formatTimeWithZone = (startTime, endTime, timezone = 'UTC') => {
  const start = DateTime.fromISO(startTime).setZone(timezone);
  const end = DateTime.fromISO(endTime).setZone(timezone);
  const timezoneAbbr = start.toFormat('ZZZZ');
  
  // Check if it's a multi-day event
  const isMultiDay = !start.hasSame(end, 'day');
  
  if (isMultiDay) {
    return `${start.toFormat('EEEE, MMMM d, h:mm a')} - ${end.toFormat('EEEE, MMMM d, h:mm a')} ${timezoneAbbr}`;
  } else {
    return `${start.toFormat('EEEE, MMMM d')} • ${start.toFormat('h:mm a')} - ${end.toFormat('h:mm a')} ${timezoneAbbr}`;
  }
};